import buffer from "buffer"
import { FC, useCallback, useEffect, useRef, useState } from "react"
import { NIL as uuidNull } from "uuid"
import { EncodingType, IdTableVariant, SearchOption } from "../../@types/enumsGlobal"
import { KizBoxEntity } from "../../Business/Dictionaries/Kiz/KizBoxEntity"
import { KizParsed } from "../../Business/Dictionaries/Kiz/KizParsed"
import { ILotByIdDTO, ILotsByIdDTO } from "../../libs/core-api-requests/accounting/lotRequest"
import { IEntitySimpleDTO } from "../../libs/coreapi-dto/@types/common"
import { GoodsDataProvider } from "../../Services/DataProviders/GoodsDataProvider"
import { KizDataProvider } from "../../Services/DataProviders/KizDataProvider"
import { LotDataProvider } from "../../Services/DataProviders/LotDataProvider"
import { cyrillicKeyboardTranslation } from "../../system/functions/keyboardLanguageTranslation"
import useGridFilter, { CreateSelectorGridFilter } from "../../system/hooks/useGridFilter"
import { useAppContext } from "../../system/providers/appContextProvider"
import { DictionaryInput } from "./dictionaryInput"
import { useDocumentTabSettings } from "../../system/hooks/useDocumentTabSettings"
import { StoreNameEnum } from "../../libs/ui-settings-manager/interfaces"
import { KizEntity } from "../../Business/Dictionaries/Kiz/KizEntity"
import LotsMultipleSelectorModal from "../../Business/Accounting/Lots/LotsMultipleSelectorModal"

interface ILotsViewItem {
    item: ILotsByIdDTO[] | null;
    show: boolean;
}

const GridSelectorInput: FC<IGridSelectorInputProps> = (props) => {
    const appContext = useAppContext()
    const lotDP = new LotDataProvider(appContext.coreApiService)
    const goodsDP = new GoodsDataProvider(appContext.coreApiService)
    const kizDP = new KizDataProvider(appContext.coreApiService)

    const [visible, setVisible] = useState<boolean>(false)
    const [searchQuery, setSearchQuery] = useState<string>(props.value ?? "")
    const [lastSearchQueries, setLastSearchQueries] = useState<string[]>([])

    const showLastSearchQuery = props.lastSearchQuerySettings?.showLastSearchQuery;
    const documentKey = props.lastSearchQuerySettings?.documentKey
    const { settings, updateSettings, getStoreUISettings } = useDocumentTabSettings(documentKey);
    const autoFocusApplied = useRef<boolean>(false);
    const [lotsViewItem, setLotsViewItem] = useState<ILotsViewItem>({ item: null, show: false });

    const timeoutRef = useRef<NodeJS.Timeout>();
    const [gridFilter, dispatchGridFilter] = useGridFilter(props.gridFilter ?? CreateSelectorGridFilter)
    const [isEnterPressed, setIsEnterPressed] = useState<boolean>(false)

    const inputRef = useRef<HTMLInputElement>(null);

    const safeTrim = (value: any): string => {
        if (typeof value === 'string') {
            return value.trim();
        }
        if (typeof value === 'number') {
            return value.toString().trim();
        }
        return '';
    };

    useEffect(() => {
        if(props.lastSearchQuerySettings?.fieldName) {
            const value = getStoreUISettings(props.lastSearchQuerySettings.fieldName) || []
            setLastSearchQueries(value)
        }
    }, [props.lastSearchQuerySettings?.fieldName])

    // Функция для сохранения запроса в массив
    const saveSearchQuery = useCallback((query: string) => {
        if (!props.lastSearchQuerySettings?.fieldName || !query || safeTrim(query).length === 0) return;
        if (!KizEntity.isKiz(query, EncodingType.base64)) return
        if (KizBoxEntity.isKizBox(query)) return
        if (KizBoxEntity.isKizBoxGismt(query)) return

        const trimmedQuery = safeTrim(query);
        
        setLastSearchQueries(prev => {
            // Создаем новый массив, исключая текущий запрос если он уже есть
            const filteredPrev = prev.filter(q => q !== trimmedQuery);
            // Добавляем новый запрос в начало и ограничиваем до 3 элементов
            const newQueries = [trimmedQuery, ...filteredPrev].slice(0, 3);
            
            const currentStore = settings.documentStore || [];
            const existingItemIndex = currentStore.findIndex(
                item => item.name === StoreNameEnum.lastSearchQuery && item.fieldName === props.lastSearchQuerySettings?.fieldName
            );

            let updatedStore;
            if (existingItemIndex >= 0) {
                updatedStore = currentStore.map((item, index) => 
                    index === existingItemIndex 
                        ? { ...item, value: newQueries }
                        : item
                );
            } else {
                updatedStore = [
                    ...currentStore,
                    {
                        name: StoreNameEnum.lastSearchQuery,
                        fieldName: props.lastSearchQuerySettings?.fieldName,
                        value: newQueries
                    }
                ];
            }

            updateSettings({
                documentStore: updatedStore
            });

            return newQueries;
        });
    }, [props.lastSearchQuerySettings?.fieldName, settings, updateSettings]);

    useEffect(() => {
        if (props.autoFocus && lastSearchQueries.length > 0 && !autoFocusApplied.current) {
            const firstQuery = lastSearchQueries[0];
            setSearchQuery(firstQuery);
            props.onChange?.(firstQuery);
            autoFocusApplied.current = true;
        }
    }, [props.autoFocus, lastSearchQueries]);
    
    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);
    
    useEffect(() => {
        if (props.gridFilter) {
            dispatchGridFilter({
                ...gridFilter,
                type: "newGridFilters",
                payload: props.gridFilter,
            })
        }
    }, [props.gridFilter])

    useEffect(() => {
        if (visible) props.setScanData?.(undefined)
    }, [visible])

    useEffect(() => {
        if (props?.selectedEntity) {
            setSearchQuery(props.numereticInput ? props?.selectedEntity : props.selectedEntity.displayName)
            //addColumnFilter(props.selectedEntity.displayName);
        } else {
            setSearchQuery("")
        }
    }, [props?.selectedEntity])

    useEffect(() => {
        if (props?.searchOption) {
            setSearchQuery("")
            Object.values(SearchOption).forEach((option) => {
                if (props.dispatchGridFilter) {
                    props.dispatchGridFilter?.({
                        type: "deleteColumnFilter",
                        payload: option,
                    })
                } else {
                    dispatchGridFilter({
                        type: "deleteColumnFilter",
                        payload: option,
                    })
                }
            })
        }
    }, [props?.searchOption])

    useEffect(() => {
        if (props?.excludeContractor)
            dispatchGridFilter?.({
                type: "addColumnFilter",
                payload: {
                    name: "IdGlobal",
                    value: props?.excludeContractor,
                    operator: "NotEq",
                } as IGridColumnFilter,
            })
    }, [props?.excludeContractor])

    const addColumnParamFilter = (payloadValue: string): void => {
        if (payloadValue != null && payloadValue.trim().length != 0) {
            if (props.dispatchGridFilter) {
                props.dispatchGridFilter?.({
                    type: "paramFilter",
                    payload: {
                        gridParamFilter: {
                            ...(gridFilter.paramFilter || {}),
                            goodsName: payloadValue,
                            idGosContractGlobal: props.gosContract,
                            idStoreGlobal: props.idStoreSupplier
                        },
                        sortDesc: "Perc",
                    },
                })
            } else {
                dispatchGridFilter?.({
                    type: "paramFilter",
                    payload: {
                        gridParamFilter: {
                            ...(gridFilter.paramFilter || {}),
                            goodsName: payloadValue,
                            idGosContractGlobal: props.gosContract,
                            idStoreGlobal: props.idStoreSupplier
                        },
                        sortDesc: "Perc",
                    },
                })
            }
        }
        if (payloadValue.trim().length == 0 && props.idStoreSupplier) {
            if (props.dispatchGridFilter) {
                props.dispatchGridFilter?.({
                    type: "paramFilter",
                    payload: {
                        gridParamFilter: {
                            ...(gridFilter.paramFilter || {}),
                            idStoreGlobal: props.idStoreSupplier
                        },
                        sortDesc: "Perc",
                    },
                })
            } else {
                dispatchGridFilter?.({
                    type: "paramFilter",
                    payload: {
                        gridParamFilter: {
                            ...(gridFilter.paramFilter || {}),
                            idStoreGlobal: props.idStoreSupplier
                        },
                        sortDesc: "Perc",
                    },
                })
            }
        }
    }

    const addColumnSimpleFilter = (payloadValue: string, nameColumn: string): void => {
        if (payloadValue) {
            dispatchGridFilter?.({
                type: "addColumnFilter",
                payload: {
                    name: nameColumn,
                    operator: "Like",
                    value: payloadValue,
                } as IGridColumnFilter,
            })
        }
    }
    const addColumnSimpleFilterNumber = (payloadValue: number, nameColumn: string): void => {
        if (payloadValue) {
            dispatchGridFilter?.({
                type: "addColumnFilter",
                payload: {
                    name: nameColumn,
                    operator: "Eq",
                    value: payloadValue,
                } as IGridColumnFilter,
            })
        }
    }

    const addColumnAndParamFilter = (payloadValue: string, nameColumn: string): void => {
        const baseParamFilter = props.idStoreSupplier ? { ...gridFilter.paramFilter, idStoreGlobal: props.idStoreSupplier } : props.gridFilter?.paramFilter;
        if (payloadValue != null && payloadValue.trim().length != 0) {
            if (props.dispatchGridFilter) {
                props.dispatchGridFilter?.({
                    type: "paramSimpleFilter",
                    payload: {
                        gridParamFilter: baseParamFilter,
                        gridColumnFilter: [
                            {
                                name: nameColumn,
                                operator: "Like",
                                value: payloadValue,
                            },
                            {
                                name: "deleted",
                                operator: "Eq",
                                value: false,
                            },
                        ]
                    }
                })
            } else {
                dispatchGridFilter?.({
                    type: "paramSimpleFilter",
                    payload: {
                        gridParamFilter: baseParamFilter,
                        gridColumnFilter: [
                            {
                                name: nameColumn,
                                operator: "Like",
                                value: payloadValue,
                            },
                            {
                                name: "deleted",
                                operator: "Eq",
                                value: false,
                            },
                        ]
                    }
                })
            }
        }
        if (payloadValue.trim().length == 0 && props.idStoreSupplier) {
            if (props.dispatchGridFilter) {
                props.dispatchGridFilter?.({
                    type: "paramSimpleFilter",
                    payload: {
                        gridParamFilter: baseParamFilter,
                        gridColumnFilter: [                            
                            {
                                name: "deleted",
                                operator: "Eq",
                                value: false,
                            },
                        ]
                    }
                })
            }else{
                dispatchGridFilter?.({
                    type: "paramSimpleFilter",
                    payload: {
                        gridParamFilter: baseParamFilter,
                        gridColumnFilter: [                           
                            {
                                name: "deleted",
                                operator: "Eq",
                                value: false,
                            },
                        ]
                    }
                })
            }
        }
    }

    const deleteColumnFilter = (): void => {
        if (props.dispatchGridFilter) {
            props.dispatchGridFilter?.({
                type: "paramSimpleFilter",
                payload: {
                    gridColumnFilter: props.gridFilter?.columnFilters,
                    gridParamFilter: props.idStoreSupplier ? { ...props.gridFilter?.paramFilter, idStoreGlobal: props.idStoreSupplier } : props.gridFilter?.paramFilter,
                },
            })
        } else
            dispatchGridFilter?.({
                type: "paramSimpleFilter",
                payload: {
                    gridColumnFilter: props.gridFilter?.columnFilters ?? CreateSelectorGridFilter.columnFilters,
                    gridParamFilter: props.idStoreSupplier ? { ...props.gridFilter?.paramFilter, idStoreGlobal: props.idStoreSupplier } : props.gridFilter?.paramFilter,
                },
            })
    }

    const selectInputText = () => {
        if (!inputRef.current) return;
        
        const input = inputRef.current;
        
        // Если input уже в фокусе
        if (document.activeElement === input) {
            // Сначала сбрасываем фокус
            input.blur();
            
            // Затем с небольшой задержкой возвращаем фокус и выделяем
            setTimeout(() => {
                input.focus();
                setTimeout(() => {
                    input.select();
                }, 10);
            }, 10);
        } else {
            // Если не в фокусе - просто выделяем
            setTimeout(() => {
                input.select();
            }, 10);
        }
    };
    
    function mainClick(value, enter: boolean) {
        enter && setIsEnterPressed(true)
        props.onFocus?.()

        if (enter && value && props.lastSearchQuerySettings?.fieldName && showLastSearchQuery) {
            saveSearchQuery(value);
        }

        let kizParsed = new KizParsed(value)

        if (kizParsed.isKiz && props.searchOption !== SearchOption.gtinSgtin && props.searchOption !== SearchOption.gtin) {
            if (value?.length && value?.length > 100 || !props.idTable) return
            lotDP.getLotByGtinSgtin(kizParsed.gtinSgtin, props.idTable as number, (lot: ILotByIdDTO) => {
                // Для РН и ПТ перезаписываем raw_barcode
                if (props.idTable === IdTableVariant.InvoiceOut || props.idTable === IdTableVariant.Movement) {
                    if (lot && lot.idKizGlobalRequested && lot.idKizGlobalRequested.length > 0 && lot.idKizGlobalRequested != uuidNull) {
                        kizDP.saveRawBarcode(lot.idKizGlobalRequested, kizParsed.rawBarcodeCorrectedBase64)
                    }
                }
                setSearchQuery("")
                props?.setScanData && props?.setScanData(kizParsed.rawBarcodeCorrected)
                props.onSelect?.(lot, kizParsed.rawBarcodeCorrected)
            }, () => selectInputText())
            return
        }

        if (props.searchOption === SearchOption.name || props.searchOption === SearchOption.goodsName) {
            if (props.valid) {
                if (props.valid()) {
                    handleOpenModal()
                    addColumnParamFilter(value)                    
                }
            } else {
                handleOpenModal()
            }
            setSearchQuery(value ?? "")
            if (enter || !enter) {
                addColumnParamFilter(value)
            }
        } else if (props.searchOption === undefined) {
            if (enter) {
                handleOpenModal()
                addColumnSimpleFilter(value, "name")
            } else if (!enter) {
                handleOpenModal()
            }
        } else if (props.searchOption === SearchOption.tradeName) {
            if (enter || !enter) {
                handleOpenModal()
                addColumnAndParamFilter(value, 'tradeNameRus')                
            }           
        } else if (props.searchOption === SearchOption.substanceName) {
            if (enter || !enter) {
                handleOpenModal()
                addColumnAndParamFilter(value, 'substanceNameRus')                
            }           
        } else if (props.searchOption === SearchOption.cureFormName) {
            if (enter || !enter) {
                handleOpenModal()
                addColumnSimpleFilter(value, 'cureFormName')                
            }           
        } else if (props.searchOption === "n_fv") {
            if (enter || !enter) {
                handleOpenModal()
                const numericValue = parseFloat(value);
                if (!isNaN(numericValue)) {
                    addColumnSimpleFilterNumber(numericValue, 'nFv')

                }
            }  
        } else if (props.searchOption === SearchOption.dosage) {
            if (enter || !enter) {
                handleOpenModal()
                addColumnSimpleFilter(value, 'dosage')                
            }           
        } else if (props.searchOption === SearchOption.gtinSgtin) {
            if (!value) {
                handleOpenModal()
                return
            }

            lotDP.getLotByGtinSgtin(value, props.idTable as number, (lot: ILotByIdDTO) => {
                const lotKizDB = lot?.kizDto?.decodedBarcode
                props?.setScanData && props?.setScanData(lotKizDB)
                props.onSelect?.(lot, lotKizDB)
                setSearchQuery("")
            }, () => selectInputText())
        } else if (props.searchOption === SearchOption.kiz) {
            if (!value) {
                handleOpenModal()
                return
            }

            lotDP.getLotByGtinSgtin(kizParsed.gtinSgtin, props.idTable as number, (lot: ILotByIdDTO) => {
                // Для РН и ПТ перезаписываем raw_barcode
                if (props.idTable === IdTableVariant.InvoiceOut || props.idTable === IdTableVariant.Movement) {
                    if (lot && lot.idKizGlobalRequested && lot.idKizGlobalRequested.length > 0 && lot.idKizGlobalRequested != uuidNull) {
                        kizDP.saveRawBarcode(lot.idKizGlobalRequested, kizParsed.rawBarcodeCorrectedBase64)
                    }
                }

                props.onSelect?.(lot, value)
                setSearchQuery("")
            }, () => selectInputText())
        } else if (props.searchOption === SearchOption.kizBox) {
            if (!value) {
                handleOpenModal()
                return
            }
            const scanDataBase64: string = buffer.Buffer.from(value, "ascii").toString("base64")
            lotDP.getLotByKizBox(scanDataBase64, (e) => {
                setVisible(false)
                props.onSelect?.(e, value)
                setSearchQuery("")
            })
        } else if (props.searchOption === SearchOption.kizOrKizBox) {
            if (!value) {
                handleOpenModal()
                return
            }
            let valueSearchQuery = value.length === 18 ? "00" + value : value
            const scanDataBase64: string = buffer.Buffer.from(valueSearchQuery, "ascii").toString("base64")
            if (KizBoxEntity.isKizBox(value)) {
                lotDP.getLotByKizBox(scanDataBase64, (e) => {
                    setVisible(false)
                    props.onSelect?.(e, valueSearchQuery)
                    setSearchQuery("")
                })
            } else {
                lotDP.getLotByGtinSgtin(kizParsed.gtinSgtin, props.idTable as number, (lot: ILotByIdDTO) => {
                    // Для РН и ПТ перезаписываем raw_barcode
                    if (props.idTable === IdTableVariant.InvoiceOut || props.idTable === IdTableVariant.Movement) {
                        if (lot && lot.idKizGlobalRequested && lot.idKizGlobalRequested.length > 0 && lot.idKizGlobalRequested != uuidNull) {
                            kizDP.saveRawBarcode(lot.idKizGlobalRequested, kizParsed.rawBarcodeCorrectedBase64)
                        }
                    }

                    props.onSelect?.(lot, valueSearchQuery)
                    setSearchQuery("")
                }, () => selectInputText())
            }
        } else if (props.searchOption === SearchOption.gtin) {
            if (!value) {
                handleOpenModal()
                return
            }
            let valueSearchQuery = value.length === 18 ? "00" + value : value
            props.onSelect?.(null, valueSearchQuery)
            setSearchQuery("")
        } else if (props.searchOption === SearchOption.barcode) {
            if (!value) {
                handleOpenModal()
                return
            }
            lotDP.findManyByInternalBarcode(value, (e) => {
                if (Array.isArray(e) && e.length > 1) {
                    setLotsViewItem({ item: e, show: true });
                    return;
                }

                props.onSelect?.(e, value)
                setSearchQuery(value ?? "")
            },
            props.idStoreGlobal
        )
        } else if (props.searchOption === SearchOption.goodsCode) {
            if (!value) {
                handleOpenModal()
                return
            }
            if (props.gosContract) {
                goodsDP.getGoodsByCodeWithGosContract(value, props.gosContract, (e) => {
                    if (e) {
                        props.onSelect?.({ displayName: e[0].goodsName.name, idGlobal: e[0].idGlobal } as IEntitySimpleDTO, value)
                        setSearchQuery(value ?? "")
                    } else {
                        addColumnSimpleFilter(value, "code")
                        handleOpenModal()
                        setSearchQuery(value ?? "")
                    }
                })
            } else {
                goodsDP.getGoodsByCode(value, (e) => {
                    if (e) {
                        props.onSelect?.({ displayName: e[0].goodsName.name, idGlobal: e[0].idGlobal } as IEntitySimpleDTO, value)
                        setSearchQuery(value ?? "")
                    } else {
                        addColumnSimpleFilter(value, "code")
                        handleOpenModal()
                        setSearchQuery(value ?? "")
                    }
                })
            }
        } else if (props.searchOption === SearchOption.goodsCodeBylot) {
            if (!value) {
                handleOpenModal()
                return
            }

            addColumnSimpleFilter(value, SearchOption.goodsCode)
            handleOpenModal()
            setSearchQuery(value ?? "")
        } else if (props.searchOption === SearchOption.internalBarcode) {
            if (!value) {
                handleOpenModal()
                return
            }
            lotDP.findManyByInternalBarcode(value, (e) => {
                if (Array.isArray(e) && e.length > 1) {
                    setLotsViewItem({ item: e, show: true });
                    return;
                }

                props.onSelect?.(e, value)
                setSearchQuery(value ?? "")
                if (props.idTable && (props.idTable === IdTableVariant.ActDisassembling || props.idTable === IdTableVariant.Movement)) {
                } else
                    handleOpenModal()
            },
            props.idStoreGlobal
        )
        }
    }

    const onClick = (e?: string): string => {
        let value = e as string;
        let trimValue = (props.searchOption === 'kizOrKizBox' || props.searchOption === 'kizBox') ? cyrillicKeyboardTranslation(value.trim()) : value;
        if (props.searchOption === SearchOption.kizBox && trimValue.length == 18 && trimValue[0] !== undefined) {
            trimValue = '00' + trimValue;
            props.onChange?.(trimValue);
            setSearchQuery(trimValue);
        } else {
            props.onChange?.(trimValue)
            setSearchQuery(trimValue)
        }
        if (trimValue != null && trimValue.length != 0) {
            //addColumnFilter(e as string);
        } else {
            deleteColumnFilter()
        }
        return trimValue
    }

    const handleOpenModal = () => {
        props.onSelectorOpen?.();
        setVisible(true);
    };

    const handleCloseModal = () => {
        props.onSelectorClose?.();
        setVisible(false);
    };

    const handleFocus = () => {
        props.onFocus?.();
        
        if (!searchQuery && lastSearchQueries.length > 0 && showLastSearchQuery) {
            const lastQuery = lastSearchQueries[0];
            setSearchQuery(lastQuery);
            props.onChange?.(lastQuery);
        }
        
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        
        timeoutRef.current = setTimeout(() => {
            if (inputRef.current) {
                inputRef.current.select();
                timeoutRef.current = undefined;
            }
        }, 10);
    };

    const handleClearLastSearchQueries = () => {
        if (!props.lastSearchQuerySettings?.showLastSearchQuery) return;
        setLastSearchQueries([])
        const updatedStore = [
            {
                name: StoreNameEnum.lastSearchQuery,
                fieldName: props.lastSearchQuerySettings?.fieldName,
                value: []
            }
        ]
        updateSettings({
            documentStore: updatedStore
        });
    }
    
    return (
        <>
            <DictionaryInput
                inputRef={inputRef}
                numereticInput={props.numereticInput}
                disabledInput={props.disabledInput}
                barcodeScan={props.barcodeScan}
                inputId={props.id}
                required={props.required}
                label={props.label}
                placeholder={props.placeholder ?? "-- Не выбрано --"}
                className={props.className}
                labelClassName={props.labelClassName}
                inline={props.inline}
                variant={props.variant}
                tabIndex={props.tabIndex}
                data-field-id={props["data-field-id"]}
                disabled={props.disabled}
                value={searchQuery}
                clues={props.clues}
                autoFocus={props.autoFocus}
                error={props.error}
                change={props.change ?? undefined}
                hiddenClearButton={props.hiddenClearButton}
                onChange={(value) => value && setSearchQuery(value)}
                onKeyDown={props.onKeyDown}
                onEnterClick={(value) => {
                    value = onClick(value)
                    mainClick(value, true)
                }}
                onMoreClick={(value) => {
                    value = onClick(value)
                    mainClick(value, false)
                }}
                onClearClick={() => {
                    deleteColumnFilter()
                    props.onChange?.("")
                    setSearchQuery("")
                    props.onClear?.()
                    handleClearLastSearchQueries()
                }}
                onFocus={showLastSearchQuery ? handleFocus : props.onFocus}
            />
            {visible && (
                <props.selectorModalJsx
                    idGlobal={props.idGlobal}
                    idTable={props?.idTable}
                    isEnabledTo={props.isEnabledTo}
                    masterIdGlobal={props.masterIdGlobal}
                    gosContract={props.gosContract}
                    gridDisplayName={props.searchOptionTitle}
                    idStoreSupplier={props.idStoreSupplier}
                    getUniqueGoodsByStore={props.getUniqueGoodsByStore}
                    onlyLotRemain={props.onlyLotRemain}
                    gridFilter={gridFilter}
                    dispatchGridFilter={props.dispatchGridFilter}
                    selectedEntity={props.selectedEntity}
                    searchQuery={searchQuery}
                    isCasheSave={props?.isCasheSave}
                    data={props?.data}
                    ok={(entity: IEntitySimpleDTO) => {
                        handleCloseModal()
                        setSearchQuery("")
                        deleteColumnFilter()
                        props.onSelect?.(props.numereticInput ? parseFloat(entity.displayName) : entity, searchQuery)
                    }}
                    cancel={(verified: boolean) => {
                        verified && props.onClear?.()
                        deleteColumnFilter()
                        handleCloseModal()
                    }}
                    setCasheData={(data) => props.setCasheData && props?.setCasheData(data)}
                    treeViewCheckDirectoryType={props.treeViewCheckDirectoryType}
                    isEnterPressed={isEnterPressed}
                    setIsEnterPressed={() => setIsEnterPressed(false)}
                    applyUserFilter={props.applyUserFilter}
                    excludeUserFilter={props.excludeUserFilter}
                    excludeContractors={props.excludeContractors}
                    activateGroupProcessing={props.activateGroupProcessing ?? false}
                    permission={props.permission}
                    documentType={props.documentType}
                />
            )}
            {lotsViewItem.item && lotsViewItem.show && (
                <LotsMultipleSelectorModal
                    ok={(value_: IEntitySimpleDTO) => {
                        props.onSelect?.(value_)
                    }}
                    cancel={() => setLotsViewItem({ item: lotsViewItem.item, show: false })}
                    data={lotsViewItem.item}
                    activateGroupProcessing={props.activateGroupProcessing}
                />
            )}
        </>
    )
}

export default GridSelectorInput
